import { isUsableAvatarSize } from "@/lib/images/avatar-image";

/**
 * Decodes an avatar URL once and answers whether it is a real photo — shared by <Avatar> (through
 * useUsableAvatarSrc) and the web notification icon (notification-avatar-icon.js). See
 * {@link isUsableAvatarSize} for the why: a placeholder photo loads successfully, so every "did the
 * image load?" signal the platform gives says yes.
 *
 * Results are cached per URL for the life of the renderer: the thread list is virtualised, so the
 * same handful of avatars mount and unmount continuously as rows recycle, and a per-mount probe
 * would re-decode them on every scroll. In-flight probes are shared too — one image URL can appear
 * on a list row, the conversation header, the participants panel and a notification at once.
 */

/** @type {Map<string, boolean>} resolved URL -> is a real photo. */
const probeResults = new Map();
/** @type {Map<string, Promise<boolean>>} probes still running, shared by every caller of that URL. */
const probesInFlight = new Map();

/**
 * @param {string} src
 * @returns {boolean | undefined} the settled answer for `src`, or undefined while it is unprobed.
 */
export function getCachedAvatarUsability(src) {
  return probeResults.get(src);
}

/**
 * @param {string} src Absolute avatar URL (browser only — needs `window.Image`).
 * @returns {Promise<boolean>} true for a real photo; false for a placeholder or a broken URL.
 */
export function probeAvatarUsability(src) {
  const settled = probeResults.get(src);
  if (settled !== undefined) return Promise.resolve(settled);
  const inFlight = probesInFlight.get(src);
  if (inFlight) return inFlight;
  const probe = new Promise((resolve) => {
    // The bytes are usually in the HTTP cache already (an <AvatarImage> rendered them), so this
    // costs a decode, not a round trip.
    const image = new window.Image();
    const settle = (usable) => {
      probeResults.set(src, usable);
      probesInFlight.delete(src);
      resolve(usable);
    };
    image.onload = () => settle(isUsableAvatarSize(image.naturalWidth, image.naturalHeight));
    // A broken/401 avatar is "no photo" as well — same outcome, the initials take over.
    image.onerror = () => settle(false);
    image.src = src;
  });
  probesInFlight.set(src, probe);
  return probe;
}
